import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AdminToolId,
  DEFAULT_ADMIN_TOOL_ID,
  adminToolPath,
  getAdminToolIdFromSlug,
} from '../../components/AdminLayout';

export type { AdminToolId };

export interface AdminViewModel {
  busy: boolean;
  activeTool: AdminToolId;
  /** Slug from the URL — null if the path is `/admin` with no tool segment. */
  routeToolSlug: string | null;
  /** True when the URL slug doesn't match any registered tool. */
  unknownSlug: boolean;
  setActiveTool: (id: AdminToolId) => void;
  initialize: () => void;
}

export function useAdminViewModel(): AdminViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  const navigate = useNavigate();
  const { tool: toolSlug } = useParams<{ tool?: string }>();

  const resolvedFromSlug = useMemo(
    () => (toolSlug ? getAdminToolIdFromSlug(toolSlug) : null),
    [toolSlug],
  );
  const activeTool: AdminToolId = resolvedFromSlug ?? DEFAULT_ADMIN_TOOL_ID;
  const unknownSlug = !!toolSlug && resolvedFromSlug === null;

  const setActiveTool = useCallback(
    (id: AdminToolId) => {
      navigate(adminToolPath(id));
    },
    [navigate],
  );

  const initialize = useCallback(() => {
    setBusy(true);
    // No async state to load yet — individual admin tools will fetch their
    // own data when selected.
    setBusy(false);
  }, []);

  return {
    busy,
    activeTool,
    routeToolSlug: toolSlug ?? null,
    unknownSlug,
    setActiveTool,
    initialize,
  };
}
